import clsx from "clsx";
import React, { useRef, useState } from "react";
import useDropdownPosition from "../../../search/criteria/components/useDropdownPosition";
import { getCriterionModalId } from "../../../search/criteria/utils";
import { useAccessWindowSize } from "../../layout/useAccessWindowSize";
import { ActionsMenu } from "../ActionsMenu";
import { ConditionalWrapper } from "../ConditionalWrapper";
import { Loader } from "../Loader";
import useModal from "../modals/useModal";
import { getDropdownClasses } from "./getDropdownClasses";
import { useDropdown } from "./useDropdown";
import { useScrollToSelected } from "./useScrollToSelected";
import useTrackDropdownKey from "./useTrackDropdownKey";
import { dropdownItemsType } from "../../types/search/search";

export interface DropdownProps {
	activeItem?: string;
	additionalClasses?: {
		dropdown?: string;
		dropdownToggle?: string;
	};
	closeOnClickOutside?: boolean;
	dropdownKey: string;
	disabled?: boolean;
	disableMobile?: boolean;
	dropdownItems?: dropdownItemsType;
	dropdownItemsEmptyText?: string;
	dropdownToggle: string | number;
	isDataCriterion?: boolean;
	label?: string;
	loading?: boolean;
	onDropdownItemClick: (key: number | string, value: string) => void;
	preselected?: boolean;
	quickSearch?: boolean;
	scrollToView?: boolean;
}

export function Dropdown({
	activeItem,
	additionalClasses,
	closeOnClickOutside,
	disabled = false,
	disableMobile = false,
	dropdownToggle,
	dropdownItems = [],
	dropdownKey,
	dropdownItemsEmptyText,
	isDataCriterion = true,
	label = "",
	loading,
	onDropdownItemClick,
	preselected = false,
	quickSearch = false,
	scrollToView = false,
}: DropdownProps) {
	const [prevToggle, setPrevToggle] = useState(dropdownToggle);
	const [toggle, setToggle] = useState(dropdownToggle);
	const {
		dropdownRef,
		dropdownIsOpened,
		onDropdownToggle,
		onDropdownItemWasSelected,
	} = useDropdown(closeOnClickOutside);
	let { isMobile } = useAccessWindowSize();
	if (disableMobile) {
		isMobile = false;
	}
	const { onOpenModal, onCloseModal } = useModal();
	const { isDropdownBelowViewport, isDropdownHidden } = useDropdownPosition(
		dropdownRef,
		dropdownIsOpened,
		quickSearch,
	);
	const { dropdownClasses, dropdownToggleClasses, dropdownMenuClasses } =
		getDropdownClasses(
			dropdownItems,
			dropdownIsOpened,
			loading,
			additionalClasses,
			isDropdownBelowViewport,
			isDropdownHidden,
			disabled,
		);
	const listRef = useRef(null);
	useTrackDropdownKey(dropdownIsOpened, listRef);
	useScrollToSelected(dropdownIsOpened, scrollToView, listRef);
	const modalId = getCriterionModalId(dropdownKey);

	if (dropdownToggle !== prevToggle) {
		setPrevToggle(dropdownToggle);
		setToggle(dropdownToggle);
	}

	const handleDropdownItemClick = (key: number | string, value: string) => {
		onDropdownToggle();
		onDropdownItemWasSelected();
		setToggle(value);
		onDropdownItemClick(key, value);
		isDataCriterion && onCloseModal(modalId);
	};

	const handleDropdownClick = () => {
		isDataCriterion && onOpenModal(modalId);
		onDropdownToggle();
	};

	const items = dropdownItems.map((item) => {
		const { key, value } = item;
		return (
			<li key={key}>
				<button
					type="button"
					className={clsx("dropdown-item", {
						active: value === activeItem,
					})}
					data-id={key}
					onClick={() => handleDropdownItemClick(key, value)}
				>
					{value}
				</button>
			</li>
		);
	});

	const dropdownMenu =
		dropdownItemsEmptyText && dropdownItems.length === 0 ? (
			<p className="dropdown-menu-placeholder">
				{dropdownItemsEmptyText}
			</p>
		) : (
			<ul ref={listRef} className="dropdown-menu-items">
				{items}
			</ul>
		);

	return (
		<div
			ref={dropdownRef}
			className={dropdownClasses}
			data-selected={preselected}
		>
			<button
				type="button"
				className={dropdownToggleClasses}
				aria-expanded={dropdownIsOpened}
				onClick={handleDropdownClick}
				disabled={disabled}
			>
				<span>{toggle}</span>
				{loading && <Loader />}
			</button>

			{dropdownIsOpened && (
				<ConditionalWrapper
					condition={isMobile && isDataCriterion}
					wrapper={(children) => (
						<ActionsMenu
							id={modalId}
							title={label}
							onClose={onCloseModal}
						>
							{children}
						</ActionsMenu>
					)}
				>
					<div
						className={dropdownMenuClasses}
						data-criterion={isDataCriterion}
						tabIndex={-1}
					>
						{dropdownMenu}
					</div>
				</ConditionalWrapper>
			)}
		</div>
	);
}
